import * as React from 'react';
import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import {
    Box, Button, Typography, Menu, MenuItem, IconButton, Snackbar, Alert,
    TextField, Select, FormControl, InputLabel, Chip, TableSortLabel, Card
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import axios from 'axios';
import AddMachineModal from './AddMachineModal';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { API_PATH, appContext } from '../../../App';
import TableLoader from '../GlobalComponents/TableLoader';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryModal from '../GlobalComponents/HistoryModal';
import AddButton from '../GlobalComponents/AddButton';
import { Machine } from './types';



export default function MachineList() {
    const [rows, setRows] = useState<Machine[]>([]);
    const [filteredRows, setFilteredRows] = useState<Machine[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Filter and search states
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [vendorFilter, setVendorFilter] = useState<string>('');
    const [coinFilter, setCoinFilter] = useState<string>('');
    const [predictionStatusFilter, setPredictionStatusFilter] = useState<string>('');
    const [inventoryStatusFilter, setInventoryStatusFilter] = useState<string>('');
    const [setupTypeFilter, setSetupTypeFilter] = useState<string>('');

    // Sort states
    const [orderBy, setOrderBy] = useState<keyof Machine>('id');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    const { authRole } = React.useContext(appContext);

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return '-';

        const dateOnly = dateString.split('T')[0];
        const date = new Date(`${dateOnly}T00:00:00Z`);

        if (Number.isNaN(date.getTime())) return '-';

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        }).format(date);
    };

    const normalizeDateForApi = (dateString?: string | null) => {
        if (!dateString) return null;

        return dateString.split('T')[0];
    };

    useEffect(() => {
        fetchMachineList();
    }, []);

    const fetchMachineList = () => {
        axios.get(API_PATH + '/machine_model/list', getAxiosConfig())
            .then(response => {
                setRows(response.data);
                setFilteredRows(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching machine list:", error);
                setError("Failed to fetch data.");
                setLoading(false);
            });
    };

    // Get unique values for filter options
    const getUniqueVendors = () => Array.from(new Set(rows.map(row => row.vendor.vendorName)));
    const getUniqueCoins = () => Array.from(new Set(rows.map(row => row.coin.coin)));
    const getInventoryStatuses = () => ['IN_STOCK', 'OUT_OF_STOCK', 'LOW_STOCK', 'PRE_ORDER'];

    // Filter and sort logic
    const applyFiltersAndSort = () => {
        let filtered = rows.filter(row => {
            const matchesSearch = row.modelName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesVendor = !vendorFilter || row.vendor.vendorName === vendorFilter;
            const matchesCoin = !coinFilter || row.coin.coin === coinFilter;
            const matchesPredictionStatus = !predictionStatusFilter ||
                (predictionStatusFilter === 'true' ? row.predictionStatus : !row.predictionStatus);
            const matchesInventoryStatus = !inventoryStatusFilter || row.inventoryStatus === inventoryStatusFilter;
            const matchesSetupType = !setupTypeFilter || row.setupType === setupTypeFilter;

            return matchesSearch && matchesVendor && matchesCoin && matchesPredictionStatus && matchesInventoryStatus && matchesSetupType;
        });

        // Sort filtered data
        filtered.sort((a, b) => {
            let aValue: any = a[orderBy];
            let bValue: any = b[orderBy];

            // Handle nested objects
            if (orderBy === 'vendor') {
                aValue = a.vendor.vendorName;
                bValue = b.vendor.vendorName;
            } else if (orderBy === 'coin') {
                aValue = a.coin.coin;
                bValue = b.coin.coin;
            }

            aValue = aValue ?? '';
            bValue = bValue ?? '';

            // Handle different data types
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });

        setFilteredRows(filtered);
    };

    // Apply filters when dependencies change
    useEffect(() => {
        applyFiltersAndSort();
    }, [rows, searchTerm, vendorFilter, coinFilter, predictionStatusFilter, inventoryStatusFilter, setupTypeFilter, orderBy, order]);

    // Handle sorting
    const handleRequestSort = (property: keyof Machine) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setVendorFilter('');
        setCoinFilter('');
        setPredictionStatusFilter('');
        setInventoryStatusFilter('');
        setSetupTypeFilter('');
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, machine: Machine) => {
        setAnchorEl(event.currentTarget);
        setSelectedMachine(machine);
    };

    const handleSubmit = (save: Machine) => {
        const formattedData = {
            id: save.id,
            modelName: save.modelName,
            coin: save.coin.coin,
            hashRate: save.hashRate,
            hashUnit: save.hashUnit,
            vendor: save.vendor.id,
            isActive: save.isActive,
            price: save.price,
            power: save.power,
            hkPrice: save.hkPrice,
            predictionStatus: save.predictionStatus,
            inventoryStatus: save.inventoryStatus,
            preOrderAvailableDate: normalizeDateForApi(save.preOrderAvailableDate),
            imageUrl: save.imageUrl || '',
            videoUrl: save.videoUrl || '',
            setupType: save.setupType || null,
            allowsFractional: save.allowsFractional || false,
            minUnitHashRate: save.minUnitHashRate ?? null,
            maxHashRate: save.maxHashRate ?? null,
        };

        setLoading(true);
        axios.post(API_PATH + '/machine_model/save', formattedData, getAxiosConfig())
            .then(response => {
                fetchMachineList();
                setLoading(false);
                handleCloseModal();
                showSnackbar('Machine saved successfully!', 'success');
            })
            .catch(error => {
                console.error("Error saving machine data:", error);
                setError("Failed to save data.");
                setLoading(false);
                showSnackbar('Failed to save machine data.', 'error');
            });
    };

    const handleOpenModal = () => setModalOpen(true);

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedMachine(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = (machine: Machine) => {
        setSelectedMachine(machine);
        setModalOpen(true);
        handleMenuClose();
    };

    const handleHistoryOpen = () => {
        setHistoryModalOpen(true);
        handleMenuClose();
    };

    const handleHistoryClose = () => setHistoryModalOpen(false);

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const downloadCSV = () => {
        if (filteredRows.length === 0) return;

        // Define CSV headers
        const headers = [
            'ID',
            'Model Name',
            'Vendor',
            'Coin',
            'Algorithm',
            'Price',
            'HK Price',
            'Power (KW)',
            'Hash Rate',
            'Hash Unit',
            'Prediction Status',
            'Inventory Status',
            'Pre-Order Available Date',
            'Setup Type',
            'Image URL',
            'Video URL'
        ];

        // Convert data to CSV format
        const csvData = filteredRows.map(row => [
            row.id || '',
            `"${row.modelName}"`,
            `"${row.vendor.vendorName}"`,
            `"${row.coin.coin}"`,
            `"${row.coin.algorithm}"`,
            row.price || '0',
            row.hkPrice || '0',
            row.power || '0',
            row.hashRate || '0',
            row.hashUnit || '',
            row.predictionStatus ? 'true' : 'false',
            row.inventoryStatus || 'IN_STOCK',
            normalizeDateForApi(row.preOrderAvailableDate) || '',
            row.setupType || '',
            `"${row.imageUrl || ''}"`,
            `"${row.videoUrl || ''}"`
        ]);

        // Combine headers and data
        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `machines_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csvText = e.target?.result as string;
                const parsedData = parseCSV(csvText);
                processCSVData(parsedData);
            } catch (error) {
                console.error('Error parsing CSV:', error);
                showSnackbar('Error parsing CSV file. Please check the format.', 'error');
            }
        };
        reader.readAsText(file);

        // Reset the input
        event.target.value = '';
    };

    const parseCSV = (csvText: string): any[] => {
        const lines = csvText.split('\n').filter(line => line.trim() !== '');
        const headers = lines[0].split(',').map(header => header.trim().replace(/^"|"$/g, ''));

        return lines.slice(1).map(line => {
            // Handle quoted fields that may contain commas
            const values: string[] = [];
            let currentValue = '';
            let insideQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                
                if (char === '"') {
                    insideQuotes = !insideQuotes;
                } else if (char === ',' && !insideQuotes) {
                    values.push(currentValue);
                    currentValue = '';
                } else {
                    currentValue += char;
                }
            }
            values.push(currentValue); // Push the last value
            
            // Clean up values (remove quotes if present)
            const cleanedValues = values.map(value => 
                value.trim().replace(/^"|"$/g, '')
            );
            
            const row: any = {};
            headers.forEach((header, index) => {
                row[header] = cleanedValues[index] || '';
            });
            return row;
        });
    };


    const processCSVData = (csvData: any[]) => {
        const updates: any[] = [];

        csvData.forEach(csvRow => {
            const machineId = parseInt(csvRow['ID']);
            const existingMachine = rows.find(row => row.id === machineId);

            if (existingMachine) {
                const newPrice = parseFloat(csvRow['Price']) || existingMachine.price;
                const newHkPrice = parseFloat(csvRow['HK Price']) || existingMachine.hkPrice;
                const newPredictionStatus = csvRow['Prediction Status']?.toLowerCase() === 'true';
                const newInventoryStatus = csvRow['Inventory Status'] || existingMachine.inventoryStatus;
                const newPreOrderAvailableDate = normalizeDateForApi(csvRow['Pre-Order Available Date']) || normalizeDateForApi(existingMachine.preOrderAvailableDate);
                const newSetupType = csvRow['Setup Type'] || existingMachine.setupType;
                const newImageUrl = csvRow['Image URL'] || existingMachine.imageUrl || '';
                const newVideoUrl = csvRow['Video URL'] || existingMachine.videoUrl || '';

                // Check if any fields have changed
                if (newPrice !== existingMachine.price ||
                    newHkPrice !== existingMachine.hkPrice ||
                    newPredictionStatus !== existingMachine.predictionStatus ||
                    newInventoryStatus !== existingMachine.inventoryStatus ||
                    newPreOrderAvailableDate !== normalizeDateForApi(existingMachine.preOrderAvailableDate) ||
                    newSetupType !== existingMachine.setupType ||
                    newImageUrl !== existingMachine.imageUrl ||
                    newVideoUrl !== existingMachine.videoUrl) {

                    updates.push({
                        id: existingMachine.id,
                        modelName: existingMachine.modelName,
                        coin: existingMachine.coin.coin,
                        hashRate: existingMachine.hashRate,
                        hashUnit: existingMachine.hashUnit,
                        vendor: existingMachine.vendor.id,
                        isActive: existingMachine.isActive,
                        price: newPrice,
                        power: existingMachine.power,
                        hkPrice: newHkPrice,
                        predictionStatus: newPredictionStatus,
                        inventoryStatus: newInventoryStatus,
                        preOrderAvailableDate: newPreOrderAvailableDate,
                        setupType: newSetupType,
                        imageUrl: newImageUrl,
                        videoUrl: newVideoUrl,
                    });
                }
            }
        });

        if (updates.length === 0) {
            showSnackbar('No changes found in the CSV file.', 'error');
            return;
        }

        // Send batch update
        updateMachinesBatch(updates);
    };

    const updateMachinesBatch = (updates: any[]) => {
        setLoading(true);

        axios.post(API_PATH + '/machine_model/batch_update', updates, getAxiosConfig())
            .then(response => {
                const result = response.data;
                fetchMachineList();

                if (result.errors && result.errors.length > 0) {
                    showSnackbar(
                        `Updated ${result.successfulUpdates} machine(s), ${result.errors.length} failed.`,
                        'error'
                    );
                    console.warn("Batch update partial failures:", result.errors);
                } else {
                    showSnackbar(`Successfully updated ${result.successfulUpdates} machine(s).`, 'success');
                }
            })
            .catch(error => {
                console.error("Error updating machines:", error);
                showSnackbar("Error updating machines.", "error");
            })
            .finally(() => {
                setLoading(false);
            });
    };


    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Paper elevation={3} sx={{ padding: 3, marginTop: 2 }}>
                {/* Filter and Search Bar */}
                <Card elevation={2} sx={{ mb: 3, p: 2, backgroundColor: 'background.default' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            Filters & Search
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={clearFilters}
                            sx={{ minWidth: 'auto' }}
                        >
                            Clear All
                        </Button>
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        {/* Search Bar */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by machine name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>

                        {/* Vendor Filter */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Vendor</InputLabel>
                                <Select
                                    value={vendorFilter}
                                    onChange={(e) => setVendorFilter(e.target.value)}
                                    label="Vendor"
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">All Vendors</MenuItem>
                                    {getUniqueVendors().map((vendor) => (
                                        <MenuItem key={vendor} value={vendor}>
                                            {vendor}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Coin Filter */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Coin</InputLabel>
                                <Select
                                    value={coinFilter}
                                    onChange={(e) => setCoinFilter(e.target.value)}
                                    label="Coin"
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">All Coins</MenuItem>
                                    {getUniqueCoins().map((coin) => (
                                        <MenuItem key={coin} value={coin}>
                                            {coin}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Prediction Status Filter */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Prediction</InputLabel>
                                <Select
                                    value={predictionStatusFilter}
                                    onChange={(e) => setPredictionStatusFilter(e.target.value)}
                                    label="Prediction"
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="true">True</MenuItem>
                                    <MenuItem value="false">False</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Inventory Status Filter */}
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Inventory Status</InputLabel>
                                <Select
                                    value={inventoryStatusFilter}
                                    onChange={(e) => setInventoryStatusFilter(e.target.value)}
                                    label="Inventory Status"
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    {getInventoryStatuses().map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status.replace('_', ' ')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Setup Type Filter */}
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Setup Type</InputLabel>
                                <Select
                                    value={setupTypeFilter}
                                    onChange={(e) => setSetupTypeFilter(e.target.value)}
                                    label="Setup Type"
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="AIR">AIR</MenuItem>
                                    <MenuItem value="HYDRO">HYDRO</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Active Filters Display */}
                    {(searchTerm || vendorFilter || coinFilter || predictionStatusFilter || inventoryStatusFilter || setupTypeFilter) && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {searchTerm && (
                                <Chip
                                    label={`Search: ${searchTerm}`}
                                    onDelete={() => setSearchTerm('')}
                                    size="small"
                                    color="primary"
                                />
                            )}
                            {vendorFilter && (
                                <Chip
                                    label={`Vendor: ${vendorFilter}`}
                                    onDelete={() => setVendorFilter('')}
                                    size="small"
                                    color="primary"
                                />
                            )}
                            {coinFilter && (
                                <Chip
                                    label={`Coin: ${coinFilter}`}
                                    onDelete={() => setCoinFilter('')}
                                    size="small"
                                    color="primary"
                                />
                            )}
                            {predictionStatusFilter && (
                                <Chip
                                    label={`Prediction: ${predictionStatusFilter}`}
                                    onDelete={() => setPredictionStatusFilter('')}
                                    size="small"
                                    color="primary"
                                />
                            )}
                            {inventoryStatusFilter && (
                                <Chip
                                    label={`Inventory: ${inventoryStatusFilter.replace('_', ' ')}`}
                                    onDelete={() => setInventoryStatusFilter('')}
                                    size="small"
                                    color="primary"
                                />
                            )}
                            {setupTypeFilter && (
                                <Chip
                                    label={`Setup: ${setupTypeFilter}`}
                                    onDelete={() => setSetupTypeFilter('')}
                                    size="small"
                                    color="primary"
                                />
                            )}
                        </Box>
                    )}
                </Card>

                {/* CSV Download and Upload Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                    >
                        Upload CSV
                        <input
                            type="file"
                            accept=".csv"
                            hidden
                            onChange={handleFileUpload}
                        />
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={downloadCSV}
                        disabled={filteredRows.length === 0}
                    >
                        Download CSV
                    </Button>
                </Box>

                {loading ? (
                    <TableLoader />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <Table sx={{ minWidth: 650 }} aria-label="machine table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '6px' }}>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'id'}
                                        direction={orderBy === 'id' ? order : 'asc'}
                                        onClick={() => handleRequestSort('id')}
                                    >
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'modelName'}
                                        direction={orderBy === 'modelName' ? order : 'asc'}
                                        onClick={() => handleRequestSort('modelName')}
                                    >
                                        Model Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'vendor'}
                                        direction={orderBy === 'vendor' ? order : 'asc'}
                                        onClick={() => handleRequestSort('vendor')}
                                    >
                                        Vendor
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'coin'}
                                        direction={orderBy === 'coin' ? order : 'asc'}
                                        onClick={() => handleRequestSort('coin')}
                                    >
                                        Coin
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Algorithm</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'price'}
                                        direction={orderBy === 'price' ? order : 'asc'}
                                        onClick={() => handleRequestSort('price')}
                                    >
                                        Price
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'hkPrice'}
                                        direction={orderBy === 'hkPrice' ? order : 'asc'}
                                        onClick={() => handleRequestSort('hkPrice')}
                                    >
                                        HK Price
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'power'}
                                        direction={orderBy === 'power' ? order : 'asc'}
                                        onClick={() => handleRequestSort('power')}
                                    >
                                        Power (KW)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'hashRate'}
                                        direction={orderBy === 'hashRate' ? order : 'asc'}
                                        onClick={() => handleRequestSort('hashRate')}
                                    >
                                        Hash Rate
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'hashUnit'}
                                        direction={orderBy === 'hashUnit' ? order : 'asc'}
                                        onClick={() => handleRequestSort('hashUnit')}
                                    >
                                        Hash Unit
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'predictionStatus'}
                                        direction={orderBy === 'predictionStatus' ? order : 'asc'}
                                        onClick={() => handleRequestSort('predictionStatus')}
                                    >
                                        Prediction Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'inventoryStatus'}
                                        direction={orderBy === 'inventoryStatus' ? order : 'asc'}
                                        onClick={() => handleRequestSort('inventoryStatus')}
                                    >
                                        Inventory Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'preOrderAvailableDate'}
                                        direction={orderBy === 'preOrderAvailableDate' ? order : 'asc'}
                                        onClick={() => handleRequestSort('preOrderAvailableDate')}
                                    >
                                        Pre-Order Available Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'setupType'}
                                        direction={orderBy === 'setupType' ? order : 'asc'}
                                        onClick={() => handleRequestSort('setupType')}
                                    >
                                        Setup Type
                                    </TableSortLabel>
                                </TableCell>
                                {authRole === 'ADMIN' && <TableCell>Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell component="th" scope="row">{row.modelName}</TableCell>
                                    <TableCell>{row.vendor.vendorName}</TableCell>
                                    <TableCell>{row.coin.coin}</TableCell>
                                    <TableCell>{row.coin.algorithm}</TableCell>
                                    <TableCell>{row.price}</TableCell>
                                    <TableCell>{row.hkPrice}</TableCell>
                                    <TableCell>{row.power}</TableCell>
                                    <TableCell>{row.hashRate}</TableCell>
                                    <TableCell>{row.hashUnit}</TableCell>
                                    <TableCell>{row.predictionStatus ? 'true' : 'false'}</TableCell>
                                    <TableCell>{row.inventoryStatus}</TableCell>
                                    <TableCell>{formatDate(row.preOrderAvailableDate)}</TableCell>
                                    <TableCell>{row.setupType || '-'}</TableCell>
                                    {authRole === 'ADMIN' &&
                                        <TableCell>
                                            <IconButton
                                                onClick={(event) => handleMenuClick(event, row)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl) && selectedMachine === row}
                                                onClose={handleMenuClose}
                                            >
                                                <MenuItem onClick={() => handleEdit(row)}>
                                                    <EditIcon sx={{ mr: 1 }} /> Edit
                                                </MenuItem>
                                                <MenuItem onClick={handleHistoryOpen}>
                                                    <HistoryIcon sx={{ mr: 1 }} /> History
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    }
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>
            {authRole === 'ADMIN' && <AddButton onClick={handleOpenModal} />}
            <AddMachineModal
                open={modalOpen}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                selectedData={selectedMachine}
            />
            <HistoryModal
                open={historyModalOpen}
                onClose={handleHistoryClose}
                selectedId={selectedMachine?.id || null}
                entity={'MachineModel'}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
